// Workforce Execution shared UI (Phase 2). KaziPeople global theme only:
// token classes (bg-card/text-foreground/text-muted-foreground/border-border/
// text-charcoal/primary/accent/destructive) — muted and accent are SURFACES,
// never bare text colors.
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { ExternalLink, Loader2, MapPin, Route as RouteIcon } from 'lucide-react';
import { Badge } from '@/features/shared/components/ui/badge';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { DateTimeField } from '@/features/shared/components/DateTimeField';
import { Label } from '@/features/shared/components/ui/label';
import { Textarea } from '@/features/shared/components/ui/textarea';
import { getAllEmployees, getMyProfile, getMySubordinates } from '@/lib/hrApi';
import { getApiErrorMessage } from '@/lib/apiError';
import {
  AssignmentEvidence, AssignmentType, PRESENCE_BADGE, WorkAssignment,
  createAssignment, updateAssignment, getAssignment, listTypes,
} from '@/services/workAssignmentService';
import { WorkItem, getWorkItemByAssignment, listWorkItems } from '@/services/workService';
import type { MyFieldDay } from '@/services/attendanceService';

/** Directory lookup: employee-service UUID → display name (AttendancePage idiom).
 * scope mirrors the server's CREATE authorization so the picker never offers
 * someone the API would refuse: 'all' (HR/owner) = whole roster; 'team'
 * (manager/employee) = self + reporting subtree — an employee with no reports
 * sees only themselves. */
export const useEmployeeNames = (scope: 'all' | 'team' = 'all') => {
  const [names, setNames] = useState<Record<string, string>>({});
  const [list, setList] = useState<Array<{ id: string; name: string }>>([]);
  // Org facets for the Workforce Overview (department / branch), read off the
  // same employee list — display only; filtering happens server-side.
  const [meta, setMeta] = useState<Record<string, EmployeeMeta>>({});
  useEffect(() => {
    let alive = true;
    const fetchScoped = async (): Promise<any[]> => {
      if (scope === 'all') return getAllEmployees();
      const [self, subs] = await Promise.all([
        getMyProfile().catch(() => null),
        getMySubordinates(true).catch(() => []),
      ]);
      const out: any[] = [];
      const seen = new Set<string>();
      [self, ...(subs || [])].forEach((e: any) => {
        if (e && e.id && !seen.has(e.id)) { seen.add(e.id); out.push(e); }
      });
      return out;
    };
    fetchScoped()
      .then((emps) => {
        if (!alive) return;
        const map: Record<string, string> = {};
        const arr: Array<{ id: string; name: string }> = [];
        const facets: Record<string, EmployeeMeta> = {};
        emps.forEach((e: any) => {
          const n = `${e.first_name || ''} ${e.last_name || ''}`.trim() || e.email || e.id;
          map[e.id] = n;
          arr.push({ id: e.id, name: n });
          facets[e.id] = {
            departmentId: e.department_id || e.department?.id || undefined,
            department: e.department_name || e.department?.name || (typeof e.department === 'string' ? e.department : undefined),
            branchId: e.branch_id || e.branch?.id || undefined,
            branch: e.branch_name || e.branch?.name || (typeof e.branch === 'string' ? e.branch : undefined),
          };
        });
        setNames(map);
        setMeta(facets);
        setList(arr.sort((a, b) => a.name.localeCompare(b.name)));
      })
      .catch(() => undefined);
    return () => { alive = false; };
  }, [scope]);
  const nameOf = useCallback(
    (id?: string | null) => (id ? names[id] || `${id.slice(0, 8)}…` : '—'),
    [names],
  );
  const metaOf = useCallback((id?: string | null): EmployeeMeta => (id && meta[id]) || {}, [meta]);
  return { nameOf, employees: list, metaOf, meta };
};

export interface EmployeeMeta {
  departmentId?: string;
  department?: string;
  branchId?: string;
  branch?: string;
}

/** Distinct {id, name} options for a facet, for filter dropdowns. */
export const facetOptions = (meta: Record<string, EmployeeMeta>, facet: 'department' | 'branch') => {
  const seen = new Map<string, string>();
  Object.values(meta).forEach((m) => {
    const id = facet === 'department' ? m.departmentId : m.branchId;
    const name = facet === 'department' ? m.department : m.branch;
    if (id && !seen.has(id)) seen.set(id, name || id.slice(0, 8));
  });
  return Array.from(seen, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
};

export const fmtDT = (v?: string) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v :
    d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
};

export const StatusBadge: React.FC<{ status: WorkAssignment['status'] }> = ({ status }) => {
  const cls: Record<string, string> = {
    draft: 'bg-muted text-foreground',
    scheduled: 'bg-muted text-muted-foreground',
    in_progress: 'bg-info-soft text-info-strong',
    completed: 'bg-success-soft text-success-strong',
    cancelled: 'bg-muted text-muted-foreground line-through',
  };
  return <Badge variant="secondary" className={cls[status] || 'bg-muted text-foreground'}>{status.replace('_', ' ')}</Badge>;
};

// Status filter chips for the assignment lists (Work Assignments, My Assignments).
// Each key is the comma-separated status string sent to the list API.
export const ASSIGN_STATUS_FILTERS = [
  { key: 'scheduled,in_progress', label: 'Active' },
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'completed', label: 'Completed' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'draft', label: 'Draft' },
];

export const ApprovalBadge: React.FC<{ status: WorkAssignment['approvalStatus'] }> = ({ status }) => {
  const cls: Record<string, string> = {
    pending: 'bg-warning-soft text-warning-strong',
    approved: 'bg-success-soft text-success-strong',
    rejected: 'bg-danger-soft text-danger-strong',
  };
  return <Badge variant="secondary" className={cls[status]}>{status}</Badge>;
};

/** "12h", "3h 30m", "45m" — whole minutes in, never a decimal on screen. */
export const fmtMinutes = (m?: number | null): string => {
  if (m == null || m < 0) return '—';
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h === 0) return `${r}m`;
  return r ? `${h}h ${r}m` : `${h}h`;
};

/** Worked hours of a checked-out assignment + (for the employee's own field
 *  days) the overtime attendance raised from it and where it stands. */
export const WorkedHoursLine: React.FC<{ a: WorkAssignment; fieldDay?: MyFieldDay | null }> = ({ a, fieldDay }) => {
  const { t } = useTranslation('admin');
  if (a.workedMinutes == null) return null;
  const ot = fieldDay?.overtime;
  const otCls: Record<string, string> = {
    pending: 'bg-warning-soft text-warning-strong',
    approved: 'bg-success-soft text-success-strong',
    rejected: 'bg-danger-soft text-danger-strong',
  };
  return (
    <span className="text-xs text-muted-foreground inline-flex flex-wrap items-center gap-1.5" data-testid="worked-hours">
      <span className="tabular-nums">
        {t('wa.hours_worked', { defaultValue: 'Hours' })}: <span className="text-foreground">{fmtMinutes(a.workedMinutes)}</span>
      </span>
      {ot && (
        <Badge variant="secondary" className={otCls[ot.status]} data-testid="overtime-chip">
          {t('wa.overtime', { defaultValue: 'Overtime' })} {fmtMinutes(ot.status === 'approved' ? (ot.minutes_approved ?? ot.minutes_requested) : ot.minutes_requested)}
          {' · '}
          {ot.status === 'approved' && ot.sent_to_payroll
            ? t('wa.ot_sent', { defaultValue: 'approved, sent to payroll' })
            : t(`wa.ot_${ot.status}`, { defaultValue: ot.status })}
        </Badge>
      )}
    </span>
  );
};

export const KindBadge: React.FC<{ kind: string; label: string }> = ({ kind, label }) => (
  <Badge variant="secondary" className={PRESENCE_BADGE[kind] || 'bg-muted text-foreground'}>{label}</Badge>
);

export const ContextLine: React.FC<{ a: WorkAssignment }> = ({ a }) => (
  <span className="text-sm text-muted-foreground inline-flex items-center gap-1.5">
    {a.origin && a.destination ? (
      <><RouteIcon className="w-3.5 h-3.5" />{a.origin} → {a.destination}</>
    ) : a.contextLabel ? (
      <><MapPin className="w-3.5 h-3.5" />{a.contextLabel}{a.contextSecondary ? ` · ${a.contextSecondary}` : ''}</>
    ) : ('—')}
  </span>
);

export const SourceChip: React.FC<{ a: WorkAssignment }> = ({ a }) => {
  if (a.source === 'internal') return null;
  const label = a.sourceProvider?.replace(/^api:/, '') || a.source;
  return a.externalUrl ? (
    <a href={a.externalUrl} target="_blank" rel="noreferrer"
      className="inline-flex items-center gap-1 text-xs text-primary-500 hover:underline">
      {label} <ExternalLink className="w-3 h-3" />
    </a>
  ) : (
    <span className="text-xs text-muted-foreground">{label}</span>
  );
};

// Verification enum → readable label + token-safe badge classes (semantic soft/
// strong tokens only). NOT_REQUIRED renders nothing — it is the no-policy case.
const VERIFICATION_BADGE: Record<string, { label: string; cls: string }> = {
  VERIFIED: { label: 'Verified', cls: 'bg-success-soft text-success-strong' },
  OUTSIDE_GEOFENCE: { label: 'Outside area', cls: 'bg-danger-soft text-danger-strong' },
  LOW_ACCURACY: { label: 'Low accuracy', cls: 'bg-warning-soft text-warning-strong' },
  LOCATION_UNAVAILABLE: { label: 'No location', cls: 'bg-muted text-foreground' },
};

// EvidenceLocation renders the manager-view location answer for one evidence row:
// the verification badge, distance from the assigned site, and the coarse address.
// Raw coordinates are precise-gated server-side — only present for authorized
// viewers, and only then do we offer a map link. Renders nothing when the type
// captures no location.
const EvidenceLocation: React.FC<{ ev: AssignmentEvidence }> = ({ ev }) => {
  const v = ev.verification && ev.verification !== 'NOT_REQUIRED'
    ? VERIFICATION_BADGE[ev.verification] : undefined;
  const hasPrecise = ev.latitude != null && ev.longitude != null;
  if (!v && ev.distanceM == null && !ev.address && !hasPrecise) return null;
  return (
    <div className="flex items-center flex-wrap gap-1.5 text-xs text-muted-foreground">
      <MapPin className="w-3 h-3" />
      {v && <Badge variant="secondary" className={v.cls}>{v.label}</Badge>}
      {ev.distanceM != null && <span className="tabular-nums">{Math.round(ev.distanceM)}m from site</span>}
      {ev.address && <span className="text-foreground">· {ev.address}</span>}
      {hasPrecise && (
        <a className="inline-flex items-center gap-0.5 text-info-strong hover:underline"
          href={`https://www.google.com/maps?q=${ev.latitude},${ev.longitude}`}
          target="_blank" rel="noopener noreferrer">
          map <ExternalLink className="w-3 h-3" />
        </a>
      )}
    </div>
  );
};

// ---- Assignment detail drawer ------------------------------------------------

/** Mirrors the server's assignment.PlanEditable: the plan may be corrected only
 *  before anyone has checked in. Kept here so every surface asks one question,
 *  and the server stays the authority — a stale view gets a refusal that
 *  explains itself rather than a button that quietly fails. */
export const PLAN_EDITABLE: WorkAssignment['status'][] = ['draft', 'scheduled'];

export const AssignmentDrawer: React.FC<{
  id: string | null; onClose: () => void; nameOf: (id?: string | null) => string;
  /** Render prop, not a node: the actions available depend on the assignment the
   *  drawer has loaded (Edit only before it starts), and only the drawer has it. */
  actions?: (a: WorkAssignment) => React.ReactNode;
}> = ({ id, onClose, nameOf, actions }) => {
  const { t } = useTranslation('admin');
  const [data, setData] = useState<{ assignment: WorkAssignment; evidence: AssignmentEvidence[] } | null>(null);
  const [linkedWork, setLinkedWork] = useState<WorkItem | null>(null);
  useEffect(() => {
    if (!id) { setData(null); setLinkedWork(null); return; }
    getAssignment(id).then(setData).catch((e) => toast.error(getApiErrorMessage(e, 'Could not load assignment.')));
    // Reverse execution-link lookup — best-effort; null = unlinked/out of reach.
    getWorkItemByAssignment(id).then(setLinkedWork).catch(() => setLinkedWork(null));
  }, [id]);
  if (!id) return null;
  const a = data?.assignment;
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-foreground/20" onClick={onClose}>
      <div className="w-full max-w-md h-full overflow-y-auto bg-background border-l border-border p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}>
        {!a ? <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /> : (
          <>
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="text-h4 text-charcoal">{a.title}</h3>
                <p className="text-sm text-muted-foreground">{nameOf(a.employeeId)} · {a.typeLabel}</p>
              </div>
              <SourceChip a={a} />
            </div>
            <div className="flex items-center gap-2">
              <StatusBadge status={a.status} />
              <ApprovalBadge status={a.approvalStatus} />
            </div>
            <div className="rounded-md border border-border bg-card p-3 space-y-1.5 text-sm">
              <ContextLine a={a} />
              <p className="text-muted-foreground">
                {t('wa.planned', { defaultValue: 'Planned' })}: <span className="text-foreground">{fmtDT(a.plannedStartAt)} → {fmtDT(a.plannedEndAt)}</span>
              </p>
              <p className="text-muted-foreground">
                {t('wa.actual', { defaultValue: 'Actual' })}: <span className="text-foreground">{fmtDT(a.actualStartAt)} → {fmtDT(a.actualEndAt)}</span>
              </p>
              <WorkedHoursLine a={a} />
              {a.description && <p className="text-foreground">{a.description}</p>}
              {a.decisionNote && (
                <p className="text-muted-foreground">{t('wa.decision_note', { defaultValue: 'Decision note' })}: <span className="text-foreground">{a.decisionNote}</span></p>
              )}
              {a.cancelReason && (
                <p className="text-danger">{t('wa.cancelled_reason', { defaultValue: 'Cancelled' })}: {a.cancelReason}</p>
              )}
              {linkedWork && (
                <p className="text-muted-foreground">
                  {t('wa.related_work_label', { defaultValue: 'Related work' })}:{' '}
                  <Link to={`../work/${linkedWork.id}`} className="text-primary-500 hover:underline">
                    {linkedWork.title}
                  </Link>
                </p>
              )}
            </div>
            <div>
              <h4 className="text-sm font-medium text-charcoal mb-2">{t('wa.evidence', { defaultValue: 'Evidence timeline' })}</h4>
              {data!.evidence.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('wa.no_evidence', { defaultValue: 'No check-ins recorded yet.' })}</p>
              ) : (
                <ul className="space-y-1.5">
                  {data!.evidence.map((ev) => (
                    <li key={ev.id} className="text-sm rounded border border-border px-2.5 py-1.5 space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-foreground">{ev.kind.replace('_', '-')}</span>
                        <span className="text-muted-foreground tabular-nums">{fmtDT(ev.occurredAt)}</span>
                        <span className="text-xs text-muted-foreground">{ev.source}</span>
                      </div>
                      <EvidenceLocation ev={ev} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {a && actions?.(a)}
          </>
        )}
      </div>
    </div>
  );
};

// ---- Create / edit dialog (type-flag-driven single form) ---------------------
//
// One form for both, because they ask for the same things. The differences are
// narrow and deliberate: when editing, the TYPE and the EMPLOYEE become read-only
// text rather than pickers. Changing either is not an edit — the type decides
// approval routing and geofence policy that have already been applied, and
// changing the employee moves whose work and whose attendance record this is.
// Both need their own action, and the server refuses them here regardless.

/** An ISO instant → the local `YYYY-MM-DDTHH:mm` that DateTimeField expects.
 *  Slicing the ISO string instead would show every stored time in UTC, so an
 *  8am visit in Dar would open the edit form reading 05:00. */
export const toLocalInput = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

export const CreateAssignmentDialog: React.FC<{
  open: boolean; onClose: () => void; onCreated: () => void;
  selfEmployeeId?: string; // set = employee self-create mode
  employees?: Array<{ id: string; name: string }>;
  /** Present = edit mode. The plan of an assignment that has not started. */
  editing?: WorkAssignment | null;
}> = ({ open, onClose, onCreated, selfEmployeeId, employees = [], editing = null }) => {
  const { t } = useTranslation('admin');
  const [types, setTypes] = useState<AssignmentType[]>([]);
  const [typeKey, setTypeKey] = useState('');
  const [form, setForm] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  // Idempotency key: minted once per dialog-open so a retried submit can never
  // duplicate the assignment (the backend returns the same row).
  const [clientKey, setClientKey] = useState('');
  // Flow C: the caller's own open work items, offered as an optional link target
  // (explicit selection only — never inferred from title similarity).
  const [myWork, setMyWork] = useState<WorkItem[]>([]);
  useEffect(() => {
    if (!open) return;
    setClientKey(`web-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
    // Editing starts from what is actually stored, and is re-read on every open
    // so a second edit never shows the first edit's abandoned draft.
    setForm(editing ? {
      employeeId: editing.employeeId || '',
      title: editing.title || '',
      description: editing.description || '',
      contextType: editing.contextType || 'client',
      contextLabel: editing.contextLabel || '',
      contextSecondary: editing.contextSecondary || '',
      origin: editing.origin || '',
      destination: editing.destination || '',
      start: toLocalInput(editing.plannedStartAt),
      end: toLocalInput(editing.plannedEndAt),
      siteLat: editing.siteLatitude != null ? String(editing.siteLatitude) : '',
      siteLng: editing.siteLongitude != null ? String(editing.siteLongitude) : '',
      siteRadius: editing.geofenceRadiusM != null ? String(editing.geofenceRadiusM) : '',
    } : {});
    if (editing?.typeKey) setTypeKey(editing.typeKey);
    if (selfEmployeeId) {
      listWorkItems({ assignee: 'me', status: 'assigned,started,in_progress,changes_requested', limit: 50 })
        .then((pg) => setMyWork(pg.items.filter((w) => !w.parent_id)))
        .catch(() => setMyWork([]));
    }
    listTypes().then((ts) => {
      const active = ts.filter((x) => x.active && (!selfEmployeeId || x.allowsSelfCreate));
      setTypes(active);
      if (active.length && !typeKey) setTypeKey(active[0].key);
    }).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing?.id]);
  if (!open) return null;
  const type = types.find((x) => x.key === typeKey);
  // The same rule the server enforces (RequiresContext + empty ContextLabel).
  // Held here as well so the form cannot be submitted into a refusal — the
  // Context TYPE dropdown is a separate field and does not satisfy it.
  const missingContext = !!type?.requiresContext && !(form.contextLabel || '').trim();
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    // Creating needs a resolved type (it decides the payload). Editing does not:
    // the type is already fixed on the row, and an assignment whose type has since
    // been deactivated is missing from `types` — bailing here would make Save do
    // nothing at all, with no message, on exactly the rows most in need of a fix.
    if (!type && !editing) return;
    if (form.start && form.end && new Date(form.end) < new Date(form.start)) {
      toast.error(t('wa.end_before_start', { defaultValue: 'The planned end must be after the planned start.' }));
      return;
    }
    // Optional site coordinates (geofence). Both must be valid numbers to send.
    const siteLat = parseFloat(form.siteLat);
    const siteLng = parseFloat(form.siteLng);
    const hasSite = (type ? type.gpsPolicy !== 'off' : true) && Number.isFinite(siteLat) && Number.isFinite(siteLng);
    const radius = parseInt(form.siteRadius, 10);
    setBusy(true);
    try {
      if (editing) {
        // Only the editable plan. employeeId and typeKey are absent on purpose —
        // the server refuses them, and sending them would imply otherwise.
        // Cleared optional text is sent as '' (an explicit clear); blank site
        // coordinates are simply omitted, so leaving them alone is the default
        // and an edit can never silently drop a geofence it never displayed.
        await updateAssignment(editing.id, {
          title: form.title,
          description: form.description || '',
          contextLabel: form.contextLabel || '',
          contextSecondary: form.contextSecondary || '',
          origin: form.origin || '',
          destination: form.destination || '',
          plannedStartAt: form.start ? new Date(form.start).toISOString() : undefined,
          plannedEndAt: form.end ? new Date(form.end).toISOString() : undefined,
          siteLatitude: hasSite ? siteLat : undefined,
          siteLongitude: hasSite ? siteLng : undefined,
          geofenceRadiusM: hasSite && Number.isFinite(radius)
            ? Math.max(20, Math.min(5000, radius)) : undefined,
        });
        toast.success(t('wa.updated', { defaultValue: 'Assignment updated.' }));
        setForm({});
        onCreated();
        onClose();
        return;
      }
      if (!type) return;
      await createAssignment({
        clientKey,
        workItemId: form.workItemId || undefined,
        employeeId: selfEmployeeId || form.employeeId,
        typeKey: type.key,
        title: form.title,
        description: form.description || undefined,
        contextType: form.contextType || (type.requiresContext ? 'client' : undefined),
        contextLabel: form.contextLabel || undefined,
        contextSecondary: form.contextSecondary || undefined,
        origin: form.origin || undefined,
        destination: form.destination || undefined,
        plannedStartAt: form.start ? new Date(form.start).toISOString() : undefined,
        plannedEndAt: form.end ? new Date(form.end).toISOString() : undefined,
        siteLatitude: hasSite ? siteLat : undefined,
        siteLongitude: hasSite ? siteLng : undefined,
        geofenceRadiusM: hasSite && Number.isFinite(radius) ? Math.max(20, Math.min(5000, radius)) : undefined,
      });
      toast.success(t('wa.created', { defaultValue: type.requiresApproval ? 'Assignment created — awaiting approval.' : 'Assignment created.' }));
      if (selfEmployeeId) {
        toast(t('wa.work_note', { defaultValue: form.workItemId ? 'Linked to your work item.' : 'It will appear in My Work too.' }));
      }
      setForm({});
      onCreated();
      onClose();
    } catch (e) {
      // The server's refusal explains itself ("it has already been checked into
      // … Cancel it instead"), so it is shown verbatim; these are only fallbacks.
      toast.error(getApiErrorMessage(e, editing
        ? t('wa.update_failed', { defaultValue: 'Could not update the assignment.' })
        : t('wa.create_failed', { defaultValue: 'Could not create the assignment.' })));
    } finally {
      setBusy(false);
    }
  };

  const sel = 'w-full rounded-md border border-border bg-background text-foreground text-sm px-2.5 py-2';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/20 p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-lg bg-background border border-border p-5 space-y-3 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <h3 className="text-h4 text-charcoal">
          {editing
            ? t('wa.edit', { defaultValue: 'Edit work assignment' })
            : selfEmployeeId
              ? t('wa.new_self', { defaultValue: 'Log a work assignment' })
              : t('wa.new', { defaultValue: 'New work assignment' })}
        </h3>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>{t('wa.type', { defaultValue: 'Type' })}</Label>
            {editing ? (
              <p className="text-sm text-muted-foreground py-2">
                {editing.typeLabel || typeKey}
                <span className="ml-2 text-xs">
                  {t('wa.type_fixed', { defaultValue: 'Changing the type means creating a new assignment.' })}
                </span>
              </p>
            ) : (
              <select className={sel} value={typeKey} onChange={(e) => setTypeKey(e.target.value)}>
                {types.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
              </select>
            )}
          </div>
          {!selfEmployeeId && (
            <div className="col-span-2">
              <Label>{t('wa.employee', { defaultValue: 'Employee' })}</Label>
              {editing ? (
                <p className="text-sm text-muted-foreground py-2">
                  {employees.find((e) => e.id === editing.employeeId)?.name || editing.employeeId}
                  <span className="ml-2 text-xs">
                    {t('wa.employee_fixed', { defaultValue: 'Reassigning is a separate action.' })}
                  </span>
                </p>
              ) : (
                <select className={sel} value={form.employeeId || ''} onChange={set('employeeId')}>
                  <option value="">{t('wa.pick_employee', { defaultValue: 'Select employee…' })}</option>
                  {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              )}
            </div>
          )}
          <div className="col-span-2">
            <Label>{t('wa.title', { defaultValue: 'Title' })}</Label>
            <Input value={form.title || ''} onChange={set('title')} placeholder="Client visit — Q3 review" />
          </div>
          {type?.requiresContext !== false && (
            <>
              <div>
                <Label>{t('wa.context_type', { defaultValue: 'Context' })}</Label>
                <select className={sel} value={form.contextType || 'client'} onChange={set('contextType')}>
                  {['client', 'project', 'route', 'site', 'delivery', 'task', 'service_job', 'campaign', 'other']
                    .map((c) => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}
                </select>
              </div>
              <div>
                <Label>{t('wa.context_label', { defaultValue: 'Client / project' })}{type?.requiresContext ? ' *' : ''}</Label>
                <Input value={form.contextLabel || ''} onChange={set('contextLabel')} placeholder="Coca-Cola Tanzania"
                  aria-invalid={missingContext || undefined} />
                {/* The server refuses without this, so say so HERE rather than
                    letting the form submit and come back with an error naming a
                    JSON key the person never saw. The Context type dropdown above
                    is a different field and does not satisfy it. */}
                {missingContext && (
                  <p className="text-xs text-danger-strong mt-1">
                    {t('wa.context_required', {
                      defaultValue: '{{type}} needs a client or project name.',
                      type: type?.label || t('wa.this_type', { defaultValue: 'This type' }),
                    })}
                  </p>
                )}
              </div>
            </>
          )}
          {(form.contextType === 'route' || form.contextType === 'delivery' || type?.category === 'route') && (
            <>
              <div>
                <Label>{t('wa.origin', { defaultValue: 'Origin' })}</Label>
                <Input value={form.origin || ''} onChange={set('origin')} />
              </div>
              <div>
                <Label>{t('wa.destination', { defaultValue: 'Destination' })}</Label>
                <Input value={form.destination || ''} onChange={set('destination')} />
              </div>
            </>
          )}
          {type?.requiresTimes !== false && (
            <>
              <div className="col-span-2">
                <Label>{t('wa.start', { defaultValue: 'Planned start' })}</Label>
                <DateTimeField value={form.start || ''} onChange={(v) => {
                  setForm((f) => {
                    const next = { ...f, start: v };
                    // Default the end to start + 2h when it's empty or now
                    // before the start — most logs then never touch it.
                    if (v && (!f.end || f.end <= v)) {
                      const d = new Date(v);
                      if (!Number.isNaN(d.getTime())) {
                        d.setHours(d.getHours() + 2);
                        const p = (n: number) => String(n).padStart(2, '0');
                        next.end = `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
                      }
                    }
                    return next;
                  });
                }} />
              </div>
              <div className="col-span-2">
                <Label>{t('wa.end', { defaultValue: 'Planned end' })}</Label>
                <DateTimeField value={form.end || ''} onChange={(v) => setForm((f) => ({ ...f, end: v }))} />
              </div>
            </>
          )}
          {type && type.gpsPolicy !== 'off' && (
            <>
              <div className="col-span-2">
                <Label>{t('wa.site_location', { defaultValue: 'Site location (for geofence)' })}</Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t('wa.site_location_hint', { defaultValue: 'Optional. In Google Maps, right-click the site → click the coordinates to copy, then paste latitude & longitude. Check-in location is compared against this point.' })}
                </p>
              </div>
              <div>
                <Label>{t('wa.site_lat', { defaultValue: 'Latitude' })}</Label>
                <Input value={form.siteLat || ''} onChange={set('siteLat')} placeholder="-6.7924" inputMode="decimal" />
              </div>
              <div>
                <Label>{t('wa.site_lng', { defaultValue: 'Longitude' })}</Label>
                <Input value={form.siteLng || ''} onChange={set('siteLng')} placeholder="39.2083" inputMode="decimal" />
              </div>
              {type.geofencePolicy !== 'off' && (
                <div>
                  <Label>{t('wa.site_radius', { defaultValue: 'Allowed radius (m)' })}</Label>
                  <Input type="number" min={20} max={5000} value={form.siteRadius || ''}
                    onChange={set('siteRadius')} placeholder={String(type.geofenceRadiusM || 200)} />
                </div>
              )}
            </>
          )}
          <div className="col-span-2">
            {selfEmployeeId && myWork.length > 0 && (
              <div className="mb-3">
                <Label>{t('wa.related_work', { defaultValue: 'Related work (optional — link instead of creating a new work item)' })}</Label>
                <select className={sel} value={form.workItemId || ''} onChange={set('workItemId')}>
                  <option value="">{t('wa.no_related', { defaultValue: 'None — track as new work' })}</option>
                  {myWork.map((w) => <option key={w.id} value={w.id}>{w.title}</option>)}
                </select>
              </div>
            )}
            <Label>{t('wa.description', { defaultValue: 'Notes' })}</Label>
            <Textarea rows={2} value={form.description || ''} onChange={set('description')} />
          </div>
        </div>
        {type?.requiresApproval && (
          <p className="text-xs text-muted-foreground">
            {t('wa.needs_approval', { defaultValue: 'This type requires approval before it counts toward presence.' })}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
          <Button onClick={submit}
            disabled={busy || !form.title || (!editing && !selfEmployeeId && !form.employeeId) || missingContext}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" />
              : editing ? t('wa.save', { defaultValue: 'Save changes' })
                : t('wa.create', { defaultValue: 'Create' })}
          </Button>
        </div>
      </div>
    </div>
  );
};
